import { useEffect, useState } from "react";
import { FileText, Download, Factory, ShieldCheck, Building2, Send, Phone, Mail, MapPin, Clock, CheckCircle2 } from "lucide-react";
import api, { API, errMsg } from "@/lib/api";
import { useSite } from "@/lib/siteDil";

const SayfaBaslik = ({ baslik, gorsel = "/site/hero.jpg" }) => (
  <section className="relative bg-slate-950 overflow-hidden">
    <img src={gorsel} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20"><span className="block h-1 w-12 bg-[var(--s-accent)] mb-4" /><h1 className="text-4xl sm:text-5xl font-extrabold text-white">{baslik}</h1></div>
  </section>
);

export const SiteKurumsal = () => {
  const { t, ic } = useSite();
  const m = ic?.metin || {};
  const bolumler = [[Building2, m.kurumsal_baslik, m.kurumsal_metin, "/site/hero.jpg"], [Factory, m.uretim_baslik, m.uretim_metin, "/site/hortum.jpg"], [ShieldCheck, m.kalite_baslik, m.kalite_metin, "/site/agir.jpg"]];
  return (
    <div data-testid="site-kurumsal">
      <SayfaBaslik baslik={t.kurumsal} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 space-y-20">
        {bolumler.map(([Ik, b, x, g], i) => (
          <section key={i} className={`grid lg:grid-cols-2 gap-10 items-center s-giris`} data-testid={`site-kurumsal-${i}`}>
            <div className={`space-y-4 ${i % 2 ? "lg:order-2" : ""}`}>
              <Ik className="w-9 h-9 text-[var(--s-accent)]" />
              <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900">{b}</h2>
              <p className="text-slate-700 leading-relaxed whitespace-pre-line">{x}</p>
            </div>
            <img src={g} alt="" loading="lazy" className="w-full h-72 object-cover rounded-md" />
          </section>
        ))}
      </div>
    </div>
  );
};

export const SiteKataloglar = () => {
  const { t, lang } = useSite();
  const [l, setL] = useState(null);
  useEffect(() => { api.get("/site/kataloglar", { params: { lang } }).then((r) => setL(r.data)).catch(() => setL([])); }, [lang]);
  return (
    <div data-testid="site-kataloglar">
      <SayfaBaslik baslik={t.kataloglar} gorsel="/site/hortum.jpg" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        {l && !l.length && <p className="text-slate-500 py-10" data-testid="site-katalog-yok">{t.katalogYok}</p>}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {l?.map((k, i) => (
            <a key={k.id} href={`${API}/site/katalog/${k.id}/dosya`} target="_blank" rel="noreferrer" data-testid={`site-katalog-${k.id}`} style={{ animationDelay: `${i * 60}ms` }}
              className="s-giris group border border-slate-200 rounded-md p-6 flex gap-4 items-start hover:shadow-lg hover:-translate-y-1 transition-[transform,box-shadow]">
              <span className="w-14 h-16 rounded-sm bg-red-50 text-red-600 flex items-center justify-center shrink-0"><FileText className="w-7 h-7" /></span>
              <span className="flex-1 min-w-0 space-y-2"><span className="block s-head font-bold text-slate-900">{k.baslik}</span>
                <span className="block text-xs text-slate-500">PDF · {(k.boyut / 1048576).toFixed(1)} MB</span>
                <span className="inline-flex items-center gap-1 text-sm font-semibold text-[var(--s-accent)]"><Download className="w-4 h-4" />{t.indir}</span></span>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
};

const Alan = ({ ad, etiket, tip = "text", f, set, zorunlu, cok, min }) => (
  <label className="block space-y-1.5"><span className="text-sm font-medium text-slate-700">{etiket}{zorunlu && " *"}</span>
    {cok ? <textarea required={zorunlu} minLength={min} rows={5} value={f[ad]} onChange={(e) => set(ad, e.target.value)} data-testid={`site-form-${ad}`} className="w-full rounded-sm border border-slate-300 px-3 py-2 text-sm outline-none focus:border-[var(--s-primary)] focus:ring-2 focus:ring-blue-100" />
      : <input required={zorunlu} minLength={min} type={tip} value={f[ad]} onChange={(e) => set(ad, e.target.value)} data-testid={`site-form-${ad}`} className="w-full h-11 rounded-sm border border-slate-300 px-3 text-sm outline-none focus:border-[var(--s-primary)] focus:ring-2 focus:ring-blue-100" />}
  </label>
);

export const SiteIletisim = () => {
  const { t, ic, lang } = useSite();
  const bos = { ad: "", firma: "", eposta: "", telefon: "", konu: "", mesaj: "", web: "" };
  const [f, setF] = useState(bos);
  const [durum, setDurum] = useState("");
  const [hata, setHata] = useState("");
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const gonder = async (e) => {
    e.preventDefault(); setDurum("gidiyor"); setHata("");
    try { await api.post("/site/iletisim", { ...f, dil: lang }); setDurum("tamam"); setF(bos); } catch (er) { setHata(errMsg(er)); setDurum(""); }
  };
  const g = ic?.genel || {};
  const harita = g.harita_adresi || (g.adres && !g.adres.startsWith("[") ? g.adres : "");
  return (
    <div data-testid="site-iletisim">
      <SayfaBaslik baslik={t.iletisim} gorsel="/site/hafif.jpg" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 grid lg:grid-cols-[1fr_1.2fr] gap-12">
        <div className="space-y-6">
          {[[Phone, t.telefon, g.telefon], [Mail, t.eposta, g.eposta], [MapPin, t.adres, g.adres], [Clock, t.saatler, g.calisma_saatleri]].map(([Ik, k, v]) => (
            <div key={k} className="flex gap-4"><span className="w-11 h-11 rounded-sm bg-[var(--s-primary)]/10 text-[var(--s-primary)] flex items-center justify-center shrink-0"><Ik className="w-5 h-5" /></span>
              <div><div className="text-xs uppercase tracking-wider text-slate-500">{k}</div><div className="font-medium text-slate-900" dir="auto">{v}</div></div></div>
          ))}
          {harita && <iframe title="map" data-testid="site-harita" src={`https://www.google.com/maps?q=${encodeURIComponent(harita)}&output=embed`} className="w-full h-72 rounded-md border border-slate-200" loading="lazy" />}
        </div>
        <form onSubmit={gonder} className="border border-slate-200 rounded-md p-6 sm:p-8 space-y-4 bg-[var(--s-surface)]" data-testid="site-iletisim-form">
          <h2 className="text-2xl font-extrabold text-slate-900">{t.formBaslik}</h2>
          {durum === "tamam" && <div className="flex gap-2 items-center p-3 rounded-sm bg-green-50 text-green-800 text-sm" data-testid="site-form-tamam"><CheckCircle2 className="w-4 h-4" />{t.tesekkur}</div>}
          {hata && <div className="p-3 rounded-sm bg-red-50 text-red-700 text-sm" data-testid="site-form-hata">{hata}</div>}
          <div className="grid sm:grid-cols-2 gap-4">
            <Alan ad="ad" etiket={t.ad} f={f} set={set} zorunlu min={2} /><Alan ad="firma" etiket={t.firma} f={f} set={set} />
            <Alan ad="eposta" etiket={t.eposta} tip="email" f={f} set={set} zorunlu /><Alan ad="telefon" etiket={t.telefon} tip="tel" f={f} set={set} />
          </div>
          <Alan ad="konu" etiket={t.konu} f={f} set={set} />
          <Alan ad="mesaj" etiket={t.mesaj} f={f} set={set} zorunlu cok min={5} />
          <input tabIndex={-1} autoComplete="off" value={f.web} onChange={(e) => set("web", e.target.value)} className="hidden" aria-hidden="true" />
          <button disabled={durum === "gidiyor"} data-testid="site-form-gonder" className="inline-flex items-center gap-2 h-12 px-8 rounded-sm bg-[var(--s-accent)] hover:bg-[var(--s-accent-h)] text-white font-semibold transition-colors disabled:opacity-60">
            <Send className="w-4 h-4 rtl:-scale-x-100" />{durum === "gidiyor" ? t.gonderiliyor : t.gonder}</button>
        </form>
      </div>
    </div>
  );
};
